import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Settings,
  Trash2,
  FileText,
  List,
  Minus,
  MessageSquare,
  Table2,
  BarChart3,
  TableProperties,
  ChevronUp,
  ChevronDown,
  Target,
  Users,
  DollarSign,
  Building,
  TrendingUp,
  FileSpreadsheet,
  Calculator,
  LayoutDashboard,
  Zap,
  Activity
} from 'lucide-react';
import { Button } from '../ui/button';

// Block type icons - including new section types
const BLOCK_ICONS = {
  cover_page: FileText,
  table_of_contents: List,
  page_break: Minus,
  narrative: MessageSquare,
  data_section: Table2,
  chart: BarChart3,
  custom_table: TableProperties,
  // Section blocks
  section_strategic_framework: Target,
  section_cost_centers: Users,
  section_cost_management: DollarSign,
  section_financial_management: Building,
  section_financial_planning: TrendingUp,
  section_financial_statements: FileSpreadsheet,
  section_financial_analytics: Calculator,
  section_planning_dashboard: LayoutDashboard,
  section_scenario_modeling: Zap,
  section_executive_dashboard: Activity,
  section_revenue_analysis: TrendingUp,
  section_cost_analysis: DollarSign,
  section_profitability: DollarSign,
  section_variance: BarChart3,
  section_trends: TrendingUp,
  section_company_profile: Building
};

// Block type colors - including new section types
const BLOCK_COLORS = {
  cover_page: 'bg-purple-50 border-purple-200',
  table_of_contents: 'bg-gray-50 border-gray-200',
  page_break: 'bg-gray-50 border-gray-200',
  narrative: 'bg-blue-50 border-blue-200',
  data_section: 'bg-green-50 border-green-200',
  chart: 'bg-amber-50 border-amber-200',
  custom_table: 'bg-pink-50 border-pink-200',
  // Input sections - blue
  section_strategic_framework: 'bg-blue-50 border-blue-200',
  section_cost_centers: 'bg-blue-50 border-blue-200',
  section_cost_management: 'bg-blue-50 border-blue-200',
  section_financial_management: 'bg-blue-50 border-blue-200',
  section_financial_planning: 'bg-blue-50 border-blue-200',
  // Output sections - green
  section_financial_statements: 'bg-green-50 border-green-200',
  section_financial_analytics: 'bg-green-50 border-green-200',
  section_planning_dashboard: 'bg-green-50 border-green-200',
  section_scenario_modeling: 'bg-green-50 border-green-200',
  // Performance sections - purple
  section_executive_dashboard: 'bg-purple-50 border-purple-200',
  section_revenue_analysis: 'bg-purple-50 border-purple-200',
  section_cost_analysis: 'bg-purple-50 border-purple-200',
  section_profitability: 'bg-purple-50 border-purple-200',
  section_variance: 'bg-purple-50 border-purple-200',
  section_trends: 'bg-purple-50 border-purple-200',
  // Company - amber
  section_company_profile: 'bg-amber-50 border-amber-200'
};

// Get block description
const getBlockDescription = (block) => {
  switch (block.type) {
    case 'narrative':
      if (block.config?.source === 'ai_generated') {
        const tone = block.config?.ai_settings?.tone || 'formal';
        const length = block.config?.ai_settings?.length || 'standard';
        return `AI-generated • ${tone} • ${length}`;
      }
      return 'Custom text';
    case 'data_section':
      const source = block.config?.source?.split('.').pop() || 'unknown';
      const level = block.config?.detail_level || 'standard';
      return `${source.replace(/_/g, ' ')} • ${level}`;
    case 'chart':
      const chartSource = block.config?.source || 'data';
      const chartType = block.config?.chart_type || 'bar';
      return `${chartType} chart • ${chartSource.replace(/_/g, ' ')}`;
    case 'custom_table':
      const cols = block.config?.columns?.length || 0;
      const rows = block.config?.rows?.length || 0;
      return `${cols} columns • ${rows} rows`;
    case 'table_of_contents':
      return 'Auto-generated';
    case 'cover_page':
      return 'Title page with branding';
    case 'page_break':
      return 'Force new page';
    default:
      // Handle section blocks
      if (block.type?.startsWith('section_')) {
        const selectedTabs = block.config?.selected_tabs || [];
        const detailLevel = block.config?.detail_level || 'standard';
        return `${selectedTabs.length} tabs • ${detailLevel}`;
      }
      return block.type?.replace(/_/g, ' ') || 'Unknown';
  }
};

const SortableBlock = ({ block, index, totalBlocks, onConfigure, onDelete, onMoveUp, onMoveDown }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined
  };

  const Icon = BLOCK_ICONS[block.type] || FileText;
  const colorClass = BLOCK_COLORS[block.type] || 'bg-gray-50 border-gray-200';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group flex items-center gap-3 p-3 rounded-lg border-2
        ${colorClass}
        ${isDragging ? 'shadow-lg opacity-90' : ''}
        transition-colors
      `}
    >
      {/* Move Up/Down Buttons */}
      <div className="flex flex-col gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0"
          onClick={onMoveUp}
          disabled={index === 0}
        >
          <ChevronUp className={`h-3 w-3 ${index === 0 ? 'text-gray-300' : 'text-gray-500'}`} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0"
          onClick={onMoveDown}
          disabled={index === totalBlocks - 1}
        >
          <ChevronDown className={`h-3 w-3 ${index === totalBlocks - 1 ? 'text-gray-300' : 'text-gray-500'}`} />
        </Button>
      </div>
      
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/50"
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>

      {/* Block Number */}
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white text-xs font-medium text-gray-600 border">
        {index + 1}
      </div>

      {/* Block Icon */}
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-white">
        <Icon className="h-4 w-4 text-gray-600" />
      </div>

      {/* Block Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {block.title}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {getBlockDescription(block)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {block.type !== 'page_break' && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onConfigure}
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default SortableBlock;
